import React from 'react';
import PropTypes from 'prop-types';
import {
  Inbox,
  Search,
  AlertTriangle,
  PackageOpen,
  FileQuestion,
} from 'lucide-react';
import styles from './EmptyState.module.scss';

/* ────────────────────────────────────────────────────────────
 * EmptyState — illustrated stand-in for a zero-rows surface.
 *
 * The contract (INVENTORY_UI_PLAN.md §6 "Aesthetic polish"):
 *   - Every list with potentially zero rows gets an EmptyState.
 *   - First-use, filtered-empty, and error states each get their
 *     own copy + CTA. The string "No data found" without art and
 *     CTA is a regression.
 *
 *   <EmptyState
 *     illustration="inbox"
 *     title="No stock entries yet"
 *     body="Add your first stock item to get started."
 *     action={<Button onClick={...}>Add stock</Button>}
 *   />
 *
 * Motion: fade + translateY on mount (MOTION.md §4.12).
 * ──────────────────────────────────────────────────────────── */

const ILLUSTRATION_MAP = {
  inbox: Inbox,
  search: Search,
  error: AlertTriangle,
  empty: PackageOpen,
  none: FileQuestion,
};

const TONE_MAP = {
  neutral: styles.toneNeutral,
  warning: styles.toneWarning,
  danger: styles.toneDanger,
};

const EmptyState = ({
  illustration,
  customIllustration,
  title,
  body,
  action,
  secondaryAction,
  tone,
  size,
  className,
}) => {
  const Icon = ILLUSTRATION_MAP[illustration] || Inbox;
  const classNames = [
    styles.empty,
    TONE_MAP[tone],
    size === 'sm' ? styles.sizeSm : '',
    className || '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={classNames} role="status">
      <div className={styles.illustration} aria-hidden="true">
        {customIllustration || <Icon strokeWidth={1.5} />}
      </div>
      {title && <h3 className={styles.title}>{title}</h3>}
      {body && <p className={styles.body}>{body}</p>}
      {(action || secondaryAction) && (
        <div className={styles.actions}>
          {action}
          {secondaryAction}
        </div>
      )}
    </div>
  );
};

EmptyState.propTypes = {
  illustration: PropTypes.oneOf(['inbox', 'search', 'error', 'empty', 'none']),
  customIllustration: PropTypes.node,
  title: PropTypes.node,
  body: PropTypes.node,
  action: PropTypes.node,
  secondaryAction: PropTypes.node,
  tone: PropTypes.oneOf(['neutral', 'warning', 'danger']),
  size: PropTypes.oneOf(['sm', 'md']),
  className: PropTypes.string,
};

EmptyState.defaultProps = {
  illustration: 'inbox',
  customIllustration: null,
  title: '',
  body: '',
  action: null,
  secondaryAction: null,
  tone: 'neutral',
  size: 'md',
  className: '',
};

export default EmptyState;
